import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { API_BASE_URL } from "../../../config/api";
import { HeatBadge } from "../../../components/players-to-watch";
import { PLAYER_HEADSHOT_FALLBACK } from "../../../config/constants";

const WIDTH = 1056;

function RenderPlayerCard({ player, playerType, headshotMap }) {
  const change = Number(player.woba_change) || 0;
  const isHot = playerType === "pitcher" ? change < 0 : change > 0;
  const deltaAbs = Math.abs(change).toFixed(3).replace(/^0\./, ".");

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
        isHot ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-slate-50"
      }`}
    >
      <img
        src={headshotMap?.[player.player_id] || PLAYER_HEADSHOT_FALLBACK}
        alt={player.player_name}
        className="w-10 h-10 rounded-full object-cover bg-gray-100 flex-shrink-0 ring-2 ring-white shadow-sm"
        onError={(e) => { e.currentTarget.src = PLAYER_HEADSHOT_FALLBACK; }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-bold text-gray-900 text-xs truncate">{player.player_name}</span>
          <HeatBadge change={player.woba_change} playerType={playerType} />
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <div>
            <span className="font-semibold text-gray-600">{player.woba_then}</span>
            <span className="text-gray-400 ml-1">THEN</span>
          </div>
          <span className="text-gray-300">→</span>
          <div>
            <span className="font-semibold text-gray-600">{player.woba_now}</span>
            <span className="text-gray-400 ml-1">NOW</span>
          </div>
          <div className={`flex items-center font-bold ${isHot ? "text-orange-500" : "text-slate-400"}`}>
            {isHot ? <ArrowUp size={10} className="mr-0.5" /> : <ArrowDown size={10} className="mr-0.5" />}
            {deltaAbs}
          </div>
        </div>
      </div>
    </div>
  );
}

function RenderWindowSection({ window: w, batters, pitchers, headshotMap }) {
  if (batters.length === 0 && pitchers.length === 0) return null;
  return (
    <div className="mb-5">
      <div className="text-[9px] font-bold uppercase tracking-widest text-gray-500 bg-gray-100 border border-gray-200 rounded px-2 py-1 inline-block mb-3">
        Last {w} PA / BF
      </div>
      <div className="grid grid-cols-2 gap-4">
        {batters.length > 0 && (
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-2">Hitters</div>
            <div className="space-y-1.5">
              {batters.map((p) => (
                <RenderPlayerCard key={p.player_id} player={p} playerType="batter" headshotMap={headshotMap} />
              ))}
            </div>
          </div>
        )}
        {pitchers.length > 0 && (
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-2">Pitchers</div>
            <div className="space-y-1.5">
              {pitchers.map((p) => (
                <RenderPlayerCard key={p.player_id} player={p} playerType="pitcher" headshotMap={headshotMap} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const PlayersToWatchRender = () => {
  const [ready, setReady] = useState(false);
  const config =
    window.__PLAYERS_TO_WATCH_CONFIG__ ||
    JSON.parse(sessionStorage.getItem("playersToWatchConfig") || "{}");
  const { sections = [], headshotMap = {}, report = {} } = config;

  useEffect(() => {
    setReady(true);
  }, []);

  return (
    <div
      data-export-ready={ready ? "true" : undefined}
      className="bg-white overflow-hidden"
      style={{ width: WIDTH }}
    >
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 px-8 py-5 flex items-center gap-4">
        {report.teamId && (
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/10 ring-2 ring-white/20 flex-shrink-0">
            <img
              src={`${API_BASE_URL}/api/team-logo/${report.teamId}`}
              alt={report.teamName}
              className="w-full h-full object-contain p-1"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        )}
        <div>
          <div className="text-2xl font-bold text-white">{report.teamName}</div>
          <div className="text-blue-200 text-sm mt-0.5">Players to Watch · {report.year}</div>
        </div>
      </div>

      <div className="px-8 py-6">
        {sections
          .filter((s) => s.batters?.length > 0 || s.pitchers?.length > 0)
          .map((s) => (
            <RenderWindowSection
              key={s.window}
              window={s.window}
              batters={s.batters || []}
              pitchers={s.pitchers || []}
              headshotMap={headshotMap}
            />
          ))}
      </div>

      <div className="absolute bottom-4 left-0 right-0 text-center">
        <span className="text-gray-300 text-[9px]">
          Generated by D3 Dashboard · {new Date().toLocaleDateString()}
        </span>
      </div>

    </div>
  );
};
